import { useEffect, useState } from "react";
import { useToast } from "../../../../../_context/ToastContext/ToastContext";
import { useDynamicForm } from "../../../../../_components/form/hooks/useDynamicForm";
import { useBillingUnitFormMutations } from "./useBillingUnitFormMutations";
import { formatBillingUnitSuccessMessage } from "../utils/billingUnitFormRules";

/**
 * State Management Layer - mirrors _features/markets/modals/MarketFormModal/
 * hooks/useMarketFormState.js exactly: the dynamic-form engine, the
 * close-confirmation dismiss guard, and submit orchestration. Directly
 * replaces the old modal's hand-rolled hasUnsavedChanges/
 * showCloseConfirmation useEffect trio (3 separate effects diffing
 * formData against originalFormData) with react-hook-form's own
 * `formState.isDirty`.
 */
export function useBillingUnitFormState(
  processedFormSchema,
  { mode, rowData, isOpen, onClose, onSuccess, refetchFilters, customBuid }
) {
  const { showToast } = useToast();
  const { submitBillingUnit, isSubmitting } = useBillingUnitFormMutations();

  const dynamicForm = useDynamicForm(processedFormSchema, { mode, rowData });
  const {
    handleSubmit: rhfHandleSubmit,
    reset,
    formState: { isDirty },
  } = dynamicForm;

  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowCloseConfirmation(false);
    }
  }, [isOpen]);

  function resetAndClose() {
    reset();
    setShowCloseConfirmation(false);
    onClose();
  }

  function attemptClose() {
    if (isDirty) {
      setShowCloseConfirmation(true);
    } else {
      resetAndClose();
    }
  }

  function cancelDiscard() {
    setShowCloseConfirmation(false);
  }

  const onSubmit = rhfHandleSubmit(async (validData) => {
    try {
      // BUID isn't a schema-driven field (see BillingUnitFormModal's own
      // Generate/Type toggle, rendered outside DynamicForm) - only sent at
      // all when the caller picked "Type manually" on create; "Generate"
      // (the default) and every edit submit omit it entirely so the
      // backend keeps assigning/preserving it exactly as before.
      const payload = mode === "create" && customBuid ? { ...validData, BUID: customBuid } : validData;
      const response = await submitBillingUnit({ mode, validData: payload, rowData });

      showToast(formatBillingUnitSuccessMessage(response, mode), "success");
      onSuccess?.(response);
      refetchFilters?.();
      resetAndClose();
    } catch (error) {
      console.error("Error:", error);
      showToast(error?.data?.message || "Something went wrong. Please try again.", "error");
    }
  });

  return {
    ...dynamicForm,
    showCloseConfirmation,
    hasUnsavedChanges: isDirty,
    isSubmitting,
    attemptClose,
    confirmDiscard: resetAndClose,
    cancelDiscard,
    onSubmit,
  };
}
